// EventLog — scrollable feed of game events (combat, building completions,
// diplomatic changes, hero events). Collapsible panel positioned bottom-right
// above the minimap.

import { ScrollArea } from '@aod/ui/components/ui/scroll-area';
import { ChevronDown, ChevronUp, ScrollText } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useT } from '../i18n/index.js';
import { appDom } from '../lib/appDom.js';
import {
  type FormattableEvent,
  eventLabel,
  formatPayload,
} from './RightPanel/formatEventPayload.js';
import {
  PanelDockGrip,
  PanelRailButton,
  PanelResizeHandle,
  useIsDesktop,
  usePanelDockStyle,
} from './layout/PanelDockControls.js';
import { usePanelLayout } from './layout/usePanelLayout.js';

interface GameEvent extends FormattableEvent {}

interface EventLogProps {
  readonly events: ReadonlyArray<GameEvent>;
  readonly maxVisible?: number;
  readonly className?: string;
  /**
   * When provided, entries with a resolvable position become buttons: the App
   * flies the camera there and pings the minimap (minimap-design.md §5).
   */
  readonly onEventClick?: (event: GameEvent) => void;
}

export function EventLog({ events, maxVisible = 50, className = '', onEventClick }: EventLogProps) {
  const t = useT();
  // Collapse lives in the panel-dock store (per player, persisted) instead of
  // local state; the dock default keeps the panel in its bottom-corner spot.
  const collapsed = usePanelLayout((s) => s.panels.eventLog.collapsed);
  const toggleCollapsed = usePanelLayout((s) => s.toggleCollapsed);
  const dock = usePanelLayout((s) => s.panels.eventLog.dock);
  const size = usePanelLayout((s) => s.panels.eventLog.size);
  const dockStyle = usePanelDockStyle('eventLog', true);
  const isDesktop = useIsDesktop();
  const viewportRef = useRef<HTMLDivElement>(null);

  const visible = events.slice(-maxVisible);
  const latest = visible[visible.length - 1];
  const latestLabel = latest ? eventLabel(t, latest.type, latest.payload) : '';
  const latestDetail = latest ? formatPayload(t, latest.type, latest.payload) : '';
  const latestAnnouncement = latest
    ? `${latestLabel}${latestDetail ? ` — ${latestDetail}` : ''}`
    : '';

  // Auto-scroll to bottom when new events arrive (if not collapsed).
  // biome-ignore lint/correctness/useExhaustiveDependencies: we intentionally only re-run on count change, not array identity.
  useEffect(() => {
    if (!collapsed && viewportRef.current) {
      const el = viewportRef.current;
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
      });
    }
  }, [events.length, collapsed]);

  if (events.length === 0) return null;

  // The live region mounts in both states — collapsing to the rail must not
  // mute the feed again (that was the original a11y bug this panel fixed).
  const liveRegion = (
    <output aria-live="polite" aria-atomic="true" className="sr-only" data-testid="event-log-live">
      {latestAnnouncement}
    </output>
  );

  if (collapsed && isDesktop) {
    return (
      <>
        <PanelRailButton
          id="eventLog"
          label={t('eventLog.title')}
          icon={<ScrollText className="size-4" />}
          testid="event-log"
        />
        {liveRegion}
      </>
    );
  }

  return (
    <div
      data-testid="event-log"
      {...appDom({
        id: 'event-log',
        type: 'List',
        role: 'list',
        scope: 'main',
        label: t('eventLog.label'),
      })}
      style={dockStyle}
      className={`absolute right-2 bottom-14 z-10 flex w-56 flex-col rounded-lg border border-border/70 bg-panel-scrim p-3 text-xs text-card-foreground shadow-lg backdrop-blur md:right-(--centre-inset) md:bottom-4 ${className}`}
    >
      {liveRegion}
      {/* Header row: the toggle stays a real button; the dock grip sits
          beside it (buttons must not nest). */}
      <div className="flex min-h-6 items-center justify-between gap-1">
        <button
          type="button"
          data-testid="event-log-toggle"
          {...appDom({ id: 'event-log-toggle', role: 'button', scope: 'main' })}
          className="flex min-h-6 flex-1 cursor-pointer items-center justify-between text-[11px] font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground"
          onClick={() => toggleCollapsed('eventLog')}
        >
          <span className="flex items-center gap-1.5">
            <ScrollText className="size-3.5" />
            {t('eventLog.title')}
            <span className="rounded-full bg-muted px-1.5 py-0 text-[9px] tabular-nums">
              {/* The history is capped (see bootSim) — the monotonic event id
                  keeps the badge truthful past the cap. */}
              {(events.at(-1)?.id ?? -1) + 1}
            </span>
          </span>
          {collapsed ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />}
        </button>
        <PanelDockGrip id="eventLog" />
      </div>

      {!collapsed && (
        <ScrollArea className={size === null ? 'mt-2 h-40' : 'mt-2 min-h-0 flex-1'}>
          <div ref={viewportRef} className="space-y-1.5 pr-2">
            {visible.map((event) => {
              const label = eventLabel(t, event.type, event.payload);
              const detail = formatPayload(t, event.type, event.payload);
              const content = (
                <>
                  <span className="font-medium">{label}</span>
                  {detail && <span className="ml-1.5 text-muted-foreground">{detail}</span>}
                </>
              );
              if (!onEventClick) {
                return (
                  <div
                    key={event.id}
                    data-testid="event-log-entry"
                    className="rounded bg-muted/60 px-2 py-1 text-[10px] leading-relaxed text-foreground"
                  >
                    {content}
                  </div>
                );
              }
              return (
                <button
                  type="button"
                  key={event.id}
                  data-testid="event-log-entry"
                  onClick={() => onEventClick(event)}
                  className="block w-full cursor-pointer rounded bg-muted/60 px-2 py-1 text-left text-[10px] leading-relaxed text-foreground hover:bg-muted"
                >
                  {content}
                </button>
              );
            })}
          </div>
        </ScrollArea>
      )}
      <PanelResizeHandle id="eventLog" side={dock === 'bottom' ? 'top' : 'right'} />
    </div>
  );
}
